import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useRecoilValue } from 'recoil';

import { apiDeleteV1Like } from '~/api/v1/likes/delete';
import { apiPostV1Likes } from '~/api/v1/likes/post';
import { useSWRV2PostById } from '~/api/v2/posts/[id]/get';
import { CardPost } from '~/components/Card/CardPost';
import { SusnetLink } from '~/components/_wrapper/SusnetLink';
import { useFetch } from '~/hooks/useFetch';
import { CurrentUserState } from '~/state/currentUser';
import { PostT } from '~/state/post';
import { isNotEmpty } from '~/utils/array';
import { getLoginUrlWithRedirectTo } from '~/utils/getLoginUrlWithRedirectTo';

import IconPin from '@public/new-icons/filled-icon/icon_pin.svg';

type Props = {
  featuredPosts?: PostT[];
  initializeObserver?: (el: HTMLElement | null) => void;
  isLoadingPosts: boolean;
  posts?: PostT[];
  uid: string;
};

export function TabPanelActivity({
  featuredPosts,
  initializeObserver,
  isLoadingPosts,
  posts,
  uid,
}: Props) {
  return (
    <div className='mx-auto my-0 flex w-11/12 flex-col items-center justify-center gap-xxl__sp py-10 lg:mx-auto lg:max-w-[1144px] lg:flex-row lg:items-start lg:gap-xl lg:py-16'>
      {/* 注目の活動報告 */}
      {isNotEmpty(featuredPosts) && (
        <div className='flex w-full flex-col gap-2 lg:w-[312px]'>
          <div className='flex w-full items-center gap-2'>
            <div className='relative h-6 w-6'>
              <IconPin />
            </div>
            <div className='text-label-medium font-bold text-secondary'>注目の活動報告</div>
          </div>

          <ul className='flex w-full flex-col gap-m__sp lg:gap-m'>
            {featuredPosts.map((post) => (
              <ActivityCardPost
                isFeatured={true}
                postId={post.id}
                uid={uid}
                key={`featured-post-${post.id}`}
              />
            ))}
          </ul>
        </div>
      )}

      {/* すべての活動報告 */}
      {isNotEmpty(posts) && (
        <div className='flex w-full flex-col gap-xl__sp lg:w-[unset] lg:max-w-[800px] lg:flex-1 lg:gap-xl'>
          <h2 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            すべての活動報告
          </h2>

          <ul className='flex flex-col gap-xl__sp lg:gap-xl'>
            {posts.map((post) => (
              <ActivityCardPost postId={post.id} uid={uid} key={`post-${post.id}`} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type ActivityCardPostProps = {
  isFeatured?: boolean;
  postId: number;
  uid: string;
  key: string;
};
function ActivityCardPost({ isFeatured = false, postId, uid, key }: ActivityCardPostProps) {
  const currentUser = useRecoilValue(CurrentUserState);
  const { post, mutate } = useSWRV2PostById(postId);

  const postUrl = `${location.origin}/corps/${uid}/${postId}`;

  /** NOTE: 投稿にいいねをしているかどうか */
  const isFavorite = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    const { id, type } = currentUser;
    const like = post?.likes?.find(({ liker_corporate, liker_organization, liker_user }) => {
      /* prettier-ignore */
      switch (type) {
        case 'corporate': return liker_corporate && liker_corporate.id === id;
        case 'org': return liker_organization && liker_organization.id === id;
        case 'user': return liker_user && liker_user.id === id;
      }
    });
    return !!like;
  }, [post?.likes, currentUser]);

  /** NOTE: いいねを押した際の挙動 */
  const { fetch, isLoading } = useFetch();
  async function onSendFavorite() {
    if (isLoading) return;
    if (!post) return;

    if (!currentUser) {
      toast.error(
        <div>
          いいねをするために
          <br />
          <SusnetLink href={getLoginUrlWithRedirectTo()} style={{ textDecoration: 'underline' }}>
            こちら
          </SusnetLink>
          からログインしてください
        </div>,
      );
      return;
    }

    toast.promise(
      fetch(async () => {
        if (isFavorite) {
          await apiDeleteV1Like({ post_id: post.id });
          await mutate();
          return 'いいねを取り消しました';
        } else {
          await apiPostV1Likes({ likable_type: 'Post', likable_id: post.id });
          await mutate();
          return 'いいねしました！';
        }
      }),
      {
        loading: '',
        success: (message) => message,
        error: 'エラーが発生しました',
      },
    );
  }

  if (!post) return null;

  if (isFeatured) {
    return (
      <li className='w-full' key={key}>
        <CardPost
          areaText={post.location?.label ?? ''}
          link={postUrl}
          src={post.content_image}
          tagList={post.tags.map((tag) => tag.name)}
          text={post.detail ?? ''}
          title={post.title ?? ''}
        />
      </li>
    );
  }

  return (
    <li className='w-full' key={key}>
      <CardPost
        areaText={post.location?.label ?? ''}
        companyLogo={post.postable_corporate?.logo}
        companyName={post.postable_corporate?.name}
        isShowCompanyInfo={true}
        link={postUrl}
        postDate={new Date(post.posted_at).toLocaleDateString()}
        src={post.content_image}
        tagList={post.tags.map((tag) => tag.name)}
        text={post.detail ?? ''}
        title={post.title ?? ''}
        isFavorite={isFavorite}
        onSendFavorite={onSendFavorite}
      />
    </li>
  );
}
